import { z } from 'zod';
import { emailSchema } from '../auth/email.js';
import { passwordSchema } from '../auth/password.js';
import { Gender, Language, Platform, Theme, UserStatus, UserType } from '../enums.js';

// Get users with pagination, search, and filters
export const getAdminUsersSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(50),
  search: z.string().optional(),
  status: z.enum(UserStatus).optional(),
  type: z.enum([UserType.PRACTITIONER, UserType.INSTITUTION]).optional(),
  sort_by: z.enum(['created_at', 'email', 'first_name', 'last_name']).default('created_at'),
  sort_order: z.enum(['asc', 'desc']).default('desc'),
});

export type GetAdminUsersPayload = z.infer<typeof getAdminUsersSchema>;

// Create user payload
export const createAdminUserSchema = z.object({
  email: emailSchema(),
  password: passwordSchema,
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  phone: z.string().optional(),
  gender: z.enum(Gender),
  language: z.enum(Language),
  theme: z.enum(Theme),
  platform: z.enum(Platform).optional(),
  push_enabled: z.boolean().optional().default(false),
});

export type CreateAdminUserPayload = z.infer<typeof createAdminUserSchema>;
export type CreateAdminUserInput = z.input<typeof createAdminUserSchema>;

// Update user payload
export const updateAdminUserSchema = z.object({
  email: emailSchema().optional(),
  first_name: z.string().min(1).optional(),
  last_name: z.string().min(1).optional(),
  phone: z.string().optional(),
  gender: z.enum(Gender).optional(),
  language: z.enum(Language).optional(),
  theme: z.enum(Theme).optional(),
  platform: z.enum(Platform).optional(),
  push_enabled: z.boolean().optional(),
});

export type UpdateAdminUserPayload = z.infer<typeof updateAdminUserSchema>;

// Update user params
export const updateAdminUserParamsSchema = z.object({
  user_id: z.uuid(),
});

export type UpdateAdminUserParamsPayload = z.infer<typeof updateAdminUserParamsSchema>;

// Update user status payload
export const updateAdminUserStatusSchema = z.object({
  status: z.enum(UserStatus),
});

export type UpdateAdminUserStatusPayload = z.infer<typeof updateAdminUserStatusSchema>;

// Update user status params
export const updateAdminUserStatusParamsSchema = z.object({
  user_id: z.uuid(),
});

export type UpdateAdminUserStatusParamsPayload = z.infer<typeof updateAdminUserStatusParamsSchema>;

// Admin user response type
export interface AdminUser {
  id: string;
  email: string;
  type: UserType;
  status: UserStatus;
  owned_institution_id: string | null;
  created_at: string;
  updated_at: string;
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    phone: string;
    gender: Gender;
    language: Language;
    theme: Theme;
    platform: Platform | null;
    push_enabled: boolean;
    avatar_url: string | null;
    created_at: string;
    updated_at: string;
  } | null;
}

// Paginated response
export interface AdminUsersResponse {
  users: AdminUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}
